import { TableRowProps as MuiTableRowProps } from '@mui/material/TableRow/TableRow';
import { CheckboxProps } from '../Checkbox';
import TableCell, { TableCellProps } from '../TableCell';
import { forwardRef, MouseEventHandler } from 'react';
import MuiTableRow, { tableRowClasses } from '@mui/material/TableRow';
import { styled } from '@mui/material/styles';
import palette from '../theme/palette';

export interface TableRowProps extends Omit<MuiTableRowProps, 'onClick'> {
  /**
   * @deprecated Please use a combination of `onClick` and `disableLinkStyling` props!
   *
   * Link prop propagated to underlying Mui component. Beware that if you want to navigate on click, you need to pass a
   * custom onClick handler!
   */
  link?: string;

  /**
   * If provided, the row will be styled as a link (i.e. include an extra chevron column and have hover css), unless
   * `disableLinkStyling` evaluates to true.
   *
   * See https://www.figma.com/file/JayWZ1azVGR83B2PDHbNRo/Moje-O2-Web-REACT-Library?node-id=2806%3A13636 for explanations.
   */
  onClick?: MuiTableRowProps['onClick'];

  /**
   * Disable styling the row as a link. Useful when you want to keep the same `onClick` for all rows but disable styling some
   * of the rows as links. BEWARE: `onClick` will still be fired!
   *
   * See https://www.figma.com/file/JayWZ1azVGR83B2PDHbNRo/Moje-O2-Web-REACT-Library?node-id=2806%3A13636 for explanations.
   */
  disableLinkStyling?: boolean;

  /**
   * Render an extra column in this row with a chevron only. Defaults to `!!onClick && !disableLinkStyling`.
   */
  hasChevron?: boolean;
  /**
   * Render an extra column in this row with a checkbox for selection. Defaults to no extra cell.
   */
  isSelectable?: boolean;
  slotProps?: {
    checkboxCell?: TableCellProps;
    chevronCell?: TableCellProps;
  };
}

interface TableRowRootProps {
  ownerState: TableRowProps & { hasLink: boolean };
  link?: string;
}

const TableRowRoot = styled(MuiTableRow, {
  name: 'O2TableRow',
  slot: 'Root',
})<TableRowRootProps>(({ theme, ownerState }) => ({
  height: 44,
  borderBottom: `1px solid ${palette.divider}`,
  [`:not(.${tableRowClasses.head}):last-of-type`]: {
    ...(!ownerState.hasLink && {
      borderBottom: 'none',
    }),
  },
  ...(ownerState.hasLink && {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.background.primary,
    },
    '&:hover > td': {
      backgroundColor: theme.palette.background.primary,
    },
  }),
}));

const ChevronCell = styled(TableCell)(() => ({
  width: 0,
}));

const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>((props, ref) => {
  const {
    children,
    disableLinkStyling,
    hasChevron: hasChevronProp,
    link: linkProp,
    onClick,
    isSelectable = false,
    slotProps,
    ...other
  } = props;

  const linkPropSet = typeof linkProp !== 'undefined';

  if (linkPropSet && process.env.NODE_ENV !== 'production')
    console.error(
      "You are using the 'link' prop, which has been deprecated in TableRow. Link-like row styling is now enabled based" +
        " on the presence of an 'onClick' handler + the false-ish-ness of 'disableLinkStyling'. Please remove the 'link' prop."
    );

  const rowIsLink = (onClick && !disableLinkStyling) || (linkPropSet && !!linkProp);

  const ownerState = {
    ...props,
    hasLink: rowIsLink,
  };

  const rootOnClick: MouseEventHandler<HTMLTableRowElement> = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  const hasChevron = typeof hasChevronProp !== 'undefined' ? hasChevronProp : !!onClick && !disableLinkStyling;

  return (
    <TableRowRoot ref={ref} onClick={rootOnClick} ownerState={ownerState} link={linkProp} {...other}>
      {isSelectable && (
        <ChevronCell
          isSelectable
          isClickable={false}
          {...{
            ...slotProps?.checkboxCell,
            slotProps: {
              ...slotProps?.checkboxCell?.slotProps,
              checkbox: {
                ...slotProps?.checkboxCell?.slotProps?.checkbox,
                color: (slotProps?.checkboxCell?.slotProps?.checkbox?.color ||
                  other.color ||
                  'default') as CheckboxProps['color'],
              },
            },
          }}
        />
      )}
      {children}
      {hasChevron && <ChevronCell align="right" hasChevron {...slotProps?.chevronCell} />}
    </TableRowRoot>
  );
});

TableRow.displayName = 'TableRow';

export default TableRow;
